/* Standalone mount of the form — the deployed page.
   Captures referral attribution from the query string and submits it with the
   application:  /apply?refsource=broker_123  (also: partner, source, utm_*).
   First-touch attribution persists in localStorage so a returning visitor
   keeps their original source even when they come back with a clean URL. */
import { createRoot } from "react-dom/client";
import { ApplicationForm } from "./ApplicationForm.jsx";
import { ErrorBoundary } from "./lib/ErrorBoundary.jsx";
import { initErrorReporting } from "./lib/sentryClient.js";
import { decodePrefill, sanitizePrefill } from "./lib/prefill.js";
import "./styles/brand.css";
import { injectBrandTheme } from "./themeBrand.js";
/* The hosted page gets the brand theme as :root overrides, exactly like the
   console, broker portal and link pages. The embeddable widget deliberately
   does NOT do this — it scopes its theme to the form wrapper (see theme.js)
   so mounting it never repaints someone else's page. */
injectBrandTheme();

initErrorReporting("form-hosted");

const q = new URLSearchParams(typeof location !== "undefined" ? location.search : "");
const opt = (k, d) => q.get(k) || d;

/* Partner hand-off prefill: /?prefill=<JSON or base64url JSON>. Decoded and
   sanitised (real fields only, no consents, saved answers win), then the
   parameter is stripped from the URL immediately so personal details don't
   linger in the address bar, history, bookmarks or analytics page-views. */
let prefill;
if (q.get("prefill")) {
  let draft = null;
  try { draft = JSON.parse(localStorage.getItem("app_form_v1") || "null"); } catch (e) { /* storage unavailable */ }
  prefill = sanitizePrefill(decodePrefill(q.get("prefill")), draft);
  try {
    q.delete("prefill");
    const qs = q.toString();
    history.replaceState(null, "", location.pathname + (qs ? "?" + qs : "") + location.hash);
  } catch (e) { /* history unavailable */ }
}

const ATTR_KEY = "pf_attribution_v1";
function getAttribution() {
  const current = {
    refsource: q.get("refsource") || q.get("partner") || q.get("source") || undefined,
    utm_source: q.get("utm_source") || undefined,
    utm_medium: q.get("utm_medium") || undefined,
    utm_campaign: q.get("utm_campaign") || undefined,
    utm_content: q.get("utm_content") || undefined,
    landing_page: typeof location !== "undefined" ? location.pathname + location.search : undefined,
  };
  const hasNew = current.refsource || current.utm_source || current.utm_campaign;
  try {
    if (hasNew) { localStorage.setItem(ATTR_KEY, JSON.stringify(current)); return current; }
    const saved = localStorage.getItem(ATTR_KEY);
    if (saved) return JSON.parse(saved);
  } catch (e) { /* storage unavailable */ }
  return hasNew ? current : undefined;
}

// Magic resume link: /?resume=<applicationId>&rt=<resumeToken>
const resume = q.get("resume") && q.get("rt") ? { applicationId: q.get("resume"), resumeToken: q.get("rt") } : undefined;

createRoot(document.getElementById("root")).render(
  <ErrorBoundary>
    <ApplicationForm
      product={opt("product", "car")}
      channel={opt("channel", "direct")}
      direction={opt("direction", "sectioned")}
      accent={opt("accent", "brand")}
      mascot={opt("mascot", "milestones")}
      attribution={getAttribution()}
      initialValues={prefill}
      resume={resume}
      submitUrl={import.meta.env.VITE_SUBMIT_URL || import.meta.env.VITE_NIMO_SUBMIT_URL || undefined}
      intake={{ debug: import.meta.env.DEV, simulateFallback: import.meta.env.DEV }}
    />
  </ErrorBoundary>
);
